import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { pushNotificationService } from '../services/pushNotificationService';
import {
  clearPushReminderState,
  getHowToEnableSteps,
  isNativePushPlatform,
  markPushPromptShownThisSession,
  markPushPromptSkipped,
  onAvailabilityResponded,
  onShowPushHowTo,
  shouldShowAfterAvailabilityResponse,
  shouldShowPeriodicPushModal
} from '../services/pushPermissionReminders';
import PermissionsPromptModal from './PermissionsPromptModal';

function PushReminderHost() {
  const [showModal, setShowModal] = useState(false);
  const [notificationDenied, setNotificationDenied] = useState(false);
  const [variant, setVariant] = useState('reminder');
  const showModalRef = useRef(false);

  const closeModal = useCallback(() => {
    showModalRef.current = false;
    setShowModal(false);
  }, []);

  const openReminder = useCallback(async (nextVariant) => {
    const status = await pushNotificationService.getReceiveStatus();
    if (status === 'granted' || status === 'not_native') {
      return;
    }

    markPushPromptShownThisSession();
    setVariant(nextVariant);
    setNotificationDenied(status === 'denied');
    showModalRef.current = true;
    setShowModal(true);
  }, []);

  useEffect(() => {
    if (!isNativePushPlatform()) {
      return undefined;
    }

    let appListener;

    const recover = async () => {
      const recovered = await pushNotificationService.recoverIfGranted();
      if (recovered) {
        clearPushReminderState();
        closeModal();
      }
    };

    recover();

    const setupResume = async () => {
      try {
        const { App } = await import('@capacitor/app');
        appListener = await App.addListener('appStateChange', async (state) => {
          if (!state.isActive) {
            return;
          }
          const recovered = await pushNotificationService.recoverIfGranted();
          if (recovered) {
            clearPushReminderState();
            closeModal();
            return;
          }

          if (showModalRef.current) {
            const status = await pushNotificationService.getReceiveStatus();
            setNotificationDenied(status === 'denied');
          }
        });
      } catch (error) {
        console.error('[PushReminderHost] Failed to listen for app resume:', error);
      }
    };

    setupResume();

    const timeoutId = window.setTimeout(async () => {
      const loggedIn = !!sessionStorage.getItem('user_id');
      const onLoginScreen = window.location.pathname === '/' || /^\/\d+$/.test(window.location.pathname);
      if (!loggedIn || onLoginScreen || !shouldShowPeriodicPushModal()) {
        return;
      }
      const granted = await pushNotificationService.checkPermissionStatus();
      if (!granted) {
        openReminder('reminder');
      }
    }, 1500);

    const unsubscribeHowTo = onShowPushHowTo(() => {
      openReminder('howto');
    });

    const unsubscribeAvailability = onAvailabilityResponded(async () => {
      const granted = await pushNotificationService.checkPermissionStatus();
      if (shouldShowAfterAvailabilityResponse(granted)) {
        openReminder('after-response');
      }
    });

    return () => {
      window.clearTimeout(timeoutId);
      unsubscribeHowTo();
      unsubscribeAvailability();
      if (appListener) {
        appListener.remove();
      }
    };
  }, [closeModal, openReminder]);

  const handleEnable = async () => {
    const result = await pushNotificationService.initialize(true);
    if (result && result.status === 'granted') {
      clearPushReminderState();
      window.dispatchEvent(new CustomEvent('fw:push-permission-granted'));
      closeModal();
      return;
    }
    setNotificationDenied(true);
  };

  const handleOpenSettings = async () => {
    await pushNotificationService.openSystemNotificationSettings();
  };

  const handleCheckAgain = async () => {
    const recovered = await pushNotificationService.recoverIfGranted();
    if (recovered) {
      clearPushReminderState();
      closeModal();
      return;
    }
    setNotificationDenied(true);
    window.alert('Notifications are still off. Open Settings, turn on Allow Notifications, then try again.');
  };

  const handleSkip = () => {
    markPushPromptSkipped();
    closeModal();
  };

  if (!showModal) {
    return null;
  }

  return (
    <PermissionsPromptModal
      showNotificationPrompt
      showBiometricPrompt={false}
      notificationDenied={notificationDenied}
      howToSteps={getHowToEnableSteps()}
      variant={variant}
      platform={Capacitor.getPlatform()}
      onEnableNotifications={handleEnable}
      onEnableBiometric={() => {}}
      onOpenSettings={handleOpenSettings}
      onCheckAgain={handleCheckAgain}
      onSkip={handleSkip}
      onClose={handleSkip}
    />
  );
}

export default PushReminderHost;
